import * as React from 'react';
import { useNavigate } from "react-router";
import {
    Card, Typography, Grid, Box, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
    CircularProgress, Alert, IconButton, Menu, MenuItem, FormControlLabel,
    Checkbox, Tooltip, Badge, TextField, InputAdornment, Switch,
    FormControl, InputLabel, Select, Divider, Avatar, Dialog,
    DialogTitle, DialogContent, DialogActions, TablePagination
} from '@mui/material';
import { useNotify } from 'react-admin';
import SportsIcon from '@mui/icons-material/Sports';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import GetAppIcon from '@mui/icons-material/GetApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WorkIcon from '@mui/icons-material/Work';
import ClearIcon from '@mui/icons-material/Clear';
import StarIcon from '@mui/icons-material/Star';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

function SquadrePage() {
    const notify = useNotify();
    const [teams, setTeams] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [fullscreen, setFullscreen] = React.useState(false);
    const [autoRefresh, setAutoRefresh] = React.useState(false);
    const [refreshInterval, setRefreshInterval] = React.useState(null);
    const [filterLega, setFilterLega] = React.useState('all');
    const [filterSport, setFilterSport] = React.useState('all');
    const [filterStatus, setFilterStatus] = React.useState('all');
    const [selectedTeam, setSelectedTeam] = React.useState(null);
    const [dialogOpen, setDialogOpen] = React.useState(false);

    // Paginazione
    const [page, setPage] = React.useState(0);
    const [limit, setLimit] = React.useState(25);
    const [total, setTotal] = React.useState(0);

    // Filtri per dropdown (caricati separatamente)
    const [leghe, setLeghe] = React.useState([]);
    const [sports, setSports] = React.useState([]);

    const [visibleColumns, setVisibleColumns] = React.useState(() => {
        const saved = localStorage.getItem('squadre-visible-columns');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                // Verifica che tutte le colonne salvate esistano in COLUMN_CONFIG
                return parsed.filter(col => Object.keys(COLUMN_CONFIG).includes(col));
            } catch (e) {
                console.warn('Errore nel parsing delle colonne salvate:', e);
            }
        }
        return ['foto', 'teamNome', 'legaDescrizione', 'sportNome', 'status', 'actions'];
    });

    const [anchorEl, setAnchorEl] = React.useState(null);
    const [sortConfig, setSortConfig] = React.useState({ key: 'teamNome', direction: 'asc' });

    // Handlers
    const navigate = useNavigate();

    const handleViewTeam = (team) => {
        navigate(`/squadre/${team.teamId}`);
    };

    const handleEditTeam = (team) => {
        navigate(`/squadre/${team.teamId}/edit`)
        // notify(`(DISATTIVO) Modifica squadra: ${team.teamNome}`, { type: 'info' });
    };

    const handleToggleVisibility = async (team) => {
        try {
            const jwt = getJwt();
            await axios.patch(
                `${host}/tbl-team/${team.teamId}`,
                { flVisibile: !team.flVisibile },
                { headers: { 'Authorization': `Bearer ${jwt}` } }
            );

            notify(`Squadra ${team.flVisibile ? 'nascosta' : 'mostrata'}`, { type: 'success' });
            loadTeams();
        } catch (error) {
            notify("Errore nell'aggiornamento", { type: 'error' });
        }
    };

    // Configurazione delle colonne
    const COLUMN_CONFIG = {
        foto: {
            label: 'Logo',
            key: 'foto',
            align: 'center',
            default: true,
            sortable: false,
            width: 80,
            render: (value, row) => (
                <Avatar
                    src={value ? `${host}/image-proxy-mw?url=${encodeURIComponent(value)}` : undefined}
                    alt={row.teamNome}
                    sx={{
                        width: 40,
                        height: 40,
                        margin: 'auto',
                        bgcolor: value ? 'transparent' : 'primary.main'
                    }}
                >
                    {!value && row.teamNome ? row.teamNome.charAt(0).toUpperCase() : '⚽'}
                </Avatar>
            )
        },
        teamNome: {
            label: 'Nome Squadra',
            key: 'teamNome',
            align: 'left',
            default: true,
            sortable: true,
            width: 200,
            render: (value, row) => (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {value || 'Nome non disponibile'}
                    </Typography>
                    {row.note && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {row.note.length > 50 ? `${row.note.substring(0, 50)}...` : row.note}
                        </Typography>
                    )}
                </Box>
            )
        },
        legaDescrizione: {
            label: 'Lega',
            key: 'legaDescrizione',
            align: 'center',
            default: true,
            sortable: true,
            width: 150,
            render: (value, row) => (
                <Box sx={{ textAlign: 'center' }}>
                    <Chip
                        label={value || `Lega ${row.legaId}`}
                        size="small"
                        sx={{
                            bgcolor: '#e3f2fd',
                            color: '#1565c0',
                            fontWeight: 500
                        }}
                        icon={<EmojiEventsIcon sx={{ fontSize: 16 }} />}
                    />
                    {/* <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        ID: {row.legaId}
                    </Typography> */}
                </Box>
            )
        },
        sportNome: {
            label: 'Sport',
            key: 'sportNome',
            align: 'center',
            default: true,
            sortable: true,
            width: 120,
            render: (value, row) => (
                <Box sx={{ textAlign: 'center' }}>
                    <Chip
                        label={value || 'Sport N/A'}
                        size="small"
                        sx={{
                            bgcolor: '#fff3e0',
                            color: '#e65100',
                            fontWeight: 500,
                            mb: 0.5
                        }}
                        icon={<SportsIcon sx={{ fontSize: 16 }} />}
                    />
                    {/* <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        ID: {row.sportId}
                    </Typography> */}
                </Box>
            )
        },
        flVisibile: {
            label: 'Stato',
            key: 'flVisibile',
            align: 'center',
            default: true,
            sortable: true,
            width: 120,
            render: (value, row) => {
                let status = 'Attiva';
                let color = '#4caf50';
                let bgcolor = '#e8f5e8';

                if (row.flAnnullato) {
                    status = 'Annullata';
                    color = '#f44336';
                    bgcolor = '#ffebee';
                } else if (!row.flAttivo) {
                    status = 'Inattiva';
                    color = '#ff9800';
                    bgcolor = '#fff3e0';
                } else if (!row.flVisibile) {
                    status = 'Nascosta';
                    color = '#9e9e9e';
                    bgcolor = '#f5f5f5';
                }

                return (
                    <Chip
                        label={status}
                        size="small"
                        sx={{ color, bgcolor, fontWeight: 500 }}
                    />
                );
            }
        },
        ordinamento: {
            label: 'Ordine',
            key: 'ordinamento',
            align: 'center',
            default: false,
            sortable: true,
            width: 80,
            render: (value) => (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <StarIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                    <Typography variant="body2">
                        {value || '-'}
                    </Typography>
                </Box>
            )
        },
        createdAt: {
            label: 'Creata il',
            key: 'createdAt',
            align: 'center',
            default: false,
            sortable: true,
            width: 120,
            render: (value) => (
                <Typography variant="body2">
                    {value ? new Date(value).toLocaleDateString('it-IT') : '-'}
                </Typography>
            )
        },
        updatedAt: {
            label: 'Aggiornata il',
            key: 'updatedAt',
            align: 'center',
            default: false,
            sortable: true,
            width: 120,
            render: (value) => (
                <Typography variant="body2">
                    {value ? new Date(value).toLocaleDateString('it-IT') : '-'}
                </Typography>
            )
        },
        actions: {
            label: 'Azioni',
            key: 'actions',
            align: 'center',
            default: true,
            sortable: false,
            width: 120,
            render: (value, row) => (
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Visualizza dettagli">
                        <IconButton size="small" onClick={() => handleViewTeam(row)}>
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Modifica">
                        <IconButton size="small" onClick={() => handleEditTeam(row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={row.flVisibile ? "Nascondi" : "Mostra"}>
                        <IconButton
                            size="small"
                            onClick={() => handleToggleVisibility(row)}
                            color={row.flVisibile ? "default" : "warning"}
                        >
                            {row.flVisibile ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    };

    // Costruisce query FeathersJS
    const buildQuery = () => {
        const query = {
            $limit: limit,
            $skip: page * limit
        };

        // Ordinamento
        if (sortConfig.key && COLUMN_CONFIG[sortConfig.key]?.sortable) {
            query.$sort = {
                [sortConfig.key]: sortConfig.direction === 'asc' ? 1 : -1
            };
        }

        // Filtri
        const filters = {};

        // Ricerca testuale (operatore $or per cercare in più campi)
        if (searchTerm.trim()) {
            const search = `%${searchTerm.trim()}%`;
            filters.$or = [
                { teamNome: { $like: search } },
                { legaDescrizione: { $like: search } },
                { sportNome: { $like: search } },
                { note: { $like: search } }
            ];
        }

        // Filtro lega
        if (filterLega !== 'all') {
            filters.legaId = parseInt(filterLega);
        }

        // Filtro sport
        if (filterSport !== 'all') {
            filters.sportId = parseInt(filterSport);
        }

        // Filtro status
        if (filterStatus !== 'all') {
            switch (filterStatus) {
                case 'active':
                    filters.flAttivo = true;
                    filters.flAnnullato = false;
                    break;
                case 'inactive':
                    filters.flAttivo = false;
                    filters.flAnnullato = false;
                    break;
                case 'hidden':
                    filters.flVisibile = false;
                    break;
                case 'cancelled':
                    filters.flAnnullato = true;
                    break;
            }
        }

        // Aggiungi filtri alla query se presenti
        if (Object.keys(filters).length > 0) {
            Object.assign(query, filters);
        }

        return query;
    };

    // Carica le squadre con paginazione e filtri FeathersJS
    const loadTeams = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();

            const query = buildQuery();

            const response = await axios.get(
                `${host}/v-team`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: query
                }
            );

            const data = response.data;
            setTeams(data.data || []);
            setTotal(data.total || 0);

        } catch (error) {
            console.error("Errore nel recupero squadre:", error);
            setError("Errore nel caricamento delle squadre");
            notify("Errore nel caricamento delle squadre", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Carica leghe per dropdown filtro
    const loadLeghe = async () => {
        try {
            const jwt = getJwt();
            const response = await axios.get(
                `${host}/tbl-lega`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        $select: ['id', 'nome'],
                        flVisibile: 1,
                        flAttivo: 1,
                        $sort: { nome: 1 }
                    }
                }
            );
            setLeghe(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento leghe:", error);
        }
    };

    // Carica sport per dropdown filtro
    const loadSports = async () => {
        try {
            const jwt = getJwt();
            const response = await axios.get(
                `${host}/tbl-sport`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        $select: ['id', 'nome'],
                        $sort: { nome: 1 }
                    }
                }
            );
            setSports(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento sport:", error);
        }
    };

    // Altri handlers
    const handleColumnToggle = (columnKey) => {
        setVisibleColumns(prev => {
            if (prev.includes(columnKey)) {
                return prev.filter(key => key !== columnKey);
            } else {
                return [...prev, columnKey];
            }
        });
    };

    const handleSort = (columnKey) => {
        if (!COLUMN_CONFIG[columnKey]?.sortable) return;

        setSortConfig(prev => ({
            key: columnKey,
            direction: prev.key === columnKey && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleLimitChange = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const resetFilters = () => {
        setSearchTerm('');
        setFilterLega('all');
        setFilterSport('all');
        setFilterStatus('all');
        setPage(0);
    };

    const resetToDefault = () => {
        setVisibleColumns(['foto', 'teamNome', 'legaDescrizione', 'sportNome', 'status', 'actions']);
        setAnchorEl(null);
    };

    const exportToCSV = () => {
        const headers = visibleColumns
            .filter(key => key !== 'actions')
            .map(key => COLUMN_CONFIG[key].label)
            .join(',');

        const rows = teams.map(team =>
            visibleColumns
                .filter(key => key !== 'actions')
                .map(key => {
                    let value = team[key] || '';
                    if (key === 'status') {
                        if (team.flAnnullato) value = 'Annullata';
                        else if (!team.flAttivo) value = 'Inattiva';
                        else if (!team.flVisibile) value = 'Nascosta';
                        else value = 'Attiva';
                    }
                    return `"${value}"`;
                }).join(',')
        ).join('\n');

        const csv = headers + '\n' + rows;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `squadre_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const toggleFullscreen = () => {
        setFullscreen(!fullscreen);
    };

    // Auto refresh
    React.useEffect(() => {
        if (autoRefresh) {
            const interval = setInterval(() => {
                loadTeams();
            }, 30000);
            setRefreshInterval(interval);
        } else {
            if (refreshInterval) {
                clearInterval(refreshInterval);
                setRefreshInterval(null);
            }
        }

        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [autoRefresh]);

    // Carica dati quando cambiano filtri, paginazione o ordinamento
    React.useEffect(() => {
        loadTeams();
    }, [page, limit, sortConfig, searchTerm, filterLega, filterSport, filterStatus]);

    // Carica leghe e sport al mount
    React.useEffect(() => {
        loadLeghe();
        loadSports();
    }, []);

    // Salva configurazione colonne
    React.useEffect(() => {
        localStorage.setItem('squadre-visible-columns', JSON.stringify(visibleColumns));
    }, [visibleColumns]);

    // Calcola statistiche (solo sui dati correnti)
    const stats = {
        totali: total,
        attive: teams.filter(t => t.flAttivo && !t.flAnnullato).length,
        inattive: teams.filter(t => !t.flAttivo && !t.flAnnullato).length,
        nascoste: teams.filter(t => !t.flVisibile).length,
        leghe: [...new Set(teams.map(t => t.legaId))].length,
        sports: [...new Set(teams.map(t => t.sportId))].length
    };

    return (
        <Box sx={{
            p: fullscreen ? 1 : { xs: 2, md: 4 },
            bgcolor: '#f6f7fb',
            minHeight: '100vh',
            position: fullscreen ? 'fixed' : 'relative',
            top: fullscreen ? 0 : 'auto',
            left: fullscreen ? 0 : 'auto',
            right: fullscreen ? 0 : 'auto',
            bottom: fullscreen ? 0 : 'auto',
            zIndex: fullscreen ? 9999 : 'auto'
        }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>

                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        ⚽ Gestione Squadre
                    </Typography>
                    {/* <Typography variant="subtitle1" sx={{ color: 'grey.600' }}>
                        Vista completa con paginazione e filtri backend FeathersJS
                    </Typography> */}
                    <Card sx={{ p: 3, borderRadius: 3, boxShadow: 2, height: 'fit-content' }}>
                        <Button

                            fullWidth
                            variant="outlined"
                            startIcon={<AddCircleIcon />}
                            sx={{
                                mb: 2,
                                color: '#4A90E2',
                                borderColor: '#4A90E2',
                                fontWeight: 600,
                                borderRadius: 2,
                                py: 1.5,
                                '&:hover': {
                                    borderColor: '#357ABD',
                                    color: '#357ABD',
                                    bgcolor: 'rgba(74, 144, 226, 0.04)'
                                }
                            }}
                            onClick={() => navigate('/squadre/create')}>
                            Nuova Squadra
                        </Button>
                    </Card>
                </Box>

                {/* Controlli superiori */}
                <Card sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: 2 }}>
                    <Grid container spacing={2} alignItems="center">

                        {/* Ricerca */}
                        <Grid item xs={12} md={2.5}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Cerca squadra, lega, sport..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>

                        {/* Filtro Lega */}
                        <Grid item xs={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Lega</InputLabel>
                                <Select
                                    value={filterLega}
                                    label="Lega"
                                    onChange={(e) => {
                                        setFilterLega(e.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="all">Tutte</MenuItem>
                                    {leghe.map(lega => (
                                        <MenuItem key={lega.id} value={lega.id}>
                                            {lega.nome || `Lega ${lega.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Filtro Sport */}
                        <Grid item xs={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Sport</InputLabel>
                                <Select
                                    value={filterSport}
                                    label="Sport"
                                    onChange={(e) => {
                                        setFilterSport(e.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="all">Tutti</MenuItem>
                                    {sports.map(sport => (
                                        <MenuItem key={sport.id} value={sport.id}>
                                            {sport.nome || `Sport ${sport.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Filtro Status */}
                        <Grid item xs={6} md={1.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Stato</InputLabel>
                                <Select
                                    value={filterStatus}
                                    label="Stato"
                                    onChange={(e) => {
                                        setFilterStatus(e.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="all">Tutti</MenuItem>
                                    <MenuItem value="active">Attive</MenuItem>
                                    <MenuItem value="inactive">Inattive</MenuItem>
                                    <MenuItem value="hidden">Nascoste</MenuItem>
                                    <MenuItem value="cancelled">Annullate</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Auto refresh */}
                        <Grid item xs={6} md={1.5}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={autoRefresh}
                                        onChange={(e) => setAutoRefresh(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label="Auto refresh"
                                sx={{ margin: 0 }}
                            />
                        </Grid>

                        {/* Controlli */}
                        <Grid item xs={12} md={2.5}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>

                                <Tooltip title="Cancella filtri">
                                    <IconButton
                                        size="small"
                                        onClick={resetFilters}
                                        disabled={searchTerm === '' && filterLega === 'all' && filterSport === 'all' && filterStatus === 'all'}
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Esporta CSV">
                                    <IconButton
                                        size="small"
                                        onClick={exportToCSV}
                                        disabled={!teams.length}
                                    >
                                        <GetAppIcon />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Configura colonne">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => setAnchorEl(e.currentTarget)}
                                    >
                                        <Badge badgeContent={visibleColumns.length} color="primary">
                                            <SettingsIcon />
                                        </Badge>
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={fullscreen ? "Esci da schermo intero" : "Schermo intero"}>
                                    <IconButton
                                        size="small"
                                        onClick={toggleFullscreen}
                                    >
                                        {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                    </IconButton>
                                </Tooltip>

                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                                    onClick={loadTeams}
                                    disabled={loading}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Aggiorna
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Card>

                {/* Menu configurazione colonne */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    PaperProps={{
                        sx: { maxHeight: 500, width: 280 }
                    }}
                >
                    <MenuItem onClick={resetToDefault}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Reset a default
                        </Typography>
                    </MenuItem>
                    <Divider />
                    {Object.entries(COLUMN_CONFIG).map(([key, config]) => (
                        <MenuItem key={key} onClick={() => handleColumnToggle(key)}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={visibleColumns.includes(key)}
                                        size="small"
                                    />
                                }
                                label={config.label}
                                sx={{ margin: 0, width: '100%' }}
                            />
                        </MenuItem>
                    ))}
                </Menu>

                {/* Statistiche rapide */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={2}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#4A90E2' }}>
                                {stats.totali}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Squadre Totali
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>
                                {stats.attive}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Attive (pagina)
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800' }}>
                                {stats.inattive}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Inattive (pagina)
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#9e9e9e' }}>
                                {stats.nascoste}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Nascoste (pagina)
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1565c0' }}>
                                {stats.leghe}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Leghe (pagina)
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#e65100' }}>
                                {stats.sports}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Sport (pagina)
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>

                {/* Tabella principale */}
                <Card sx={{ borderRadius: 3, boxShadow: 2 }}>

                    {error && (
                        <Alert severity="error" sx={{ m: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loading && !teams.length ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                            <CircularProgress size={40} />
                        </Box>
                    ) : teams.length > 0 ? (
                        <>
                            <TableContainer sx={{ maxHeight: fullscreen ? 'calc(100vh - 400px)' : 600 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            {visibleColumns
                                                .filter(columnKey => COLUMN_CONFIG[columnKey]) // Filtra colonne esistenti
                                                .map(columnKey => {
                                                    const config = COLUMN_CONFIG[columnKey];
                                                    return (
                                                        <TableCell
                                                            key={columnKey}
                                                            align={config.align}
                                                            sx={{
                                                                fontWeight: 600,
                                                                width: config.width,
                                                                cursor: config.sortable ? 'pointer' : 'default',
                                                                bgcolor: sortConfig.key === columnKey ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                                                                '&:hover': config.sortable ? {
                                                                    bgcolor: 'rgba(0, 0, 0, 0.04)'
                                                                } : {},
                                                                borderBottom: '2px solid #e0e0e0'
                                                            }}
                                                            onClick={() => handleSort(columnKey)}
                                                        >
                                                            {config.label}
                                                            {sortConfig.key === columnKey && (
                                                                <span style={{ marginLeft: 4 }}>
                                                                    {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                                </span>
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {teams.map((team, index) => (
                                            <TableRow
                                                key={team.teamId}
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0, 0, 0, 0.04)'
                                                    },
                                                    bgcolor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'inherit',
                                                    opacity: team.flAnnullato ? 0.6 : 1
                                                }}
                                            >
                                                {visibleColumns
                                                    .filter(columnKey => COLUMN_CONFIG[columnKey]) // Filtra colonne esistenti
                                                    .map(columnKey => {
                                                        const config = COLUMN_CONFIG[columnKey];
                                                        const value = team[config.key];

                                                        return (
                                                            <TableCell
                                                                key={columnKey}
                                                                align={config.align}
                                                                sx={{
                                                                    width: config.width,
                                                                    py: 1
                                                                }}
                                                            >
                                                                {config.render ?
                                                                    config.render(value, team, index) :
                                                                    value || '-'
                                                                }
                                                            </TableCell>
                                                        );
                                                    })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Paginazione */}
                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={handlePageChange}
                                rowsPerPage={limit}
                                onRowsPerPageChange={handleLimitChange}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                labelRowsPerPage="Righe per pagina:"
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
                                }
                                sx={{
                                    borderTop: '1px solid #e0e0e0',
                                    '& .MuiTablePagination-toolbar': {
                                        px: 2
                                    }
                                }}
                            />
                        </>
                    ) : (
                        <Box sx={{ textAlign: 'center', p: 6 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {searchTerm || filterLega !== 'all' || filterSport !== 'all' || filterStatus !== 'all' ?
                                    'Nessuna squadra trovata con i filtri applicati' :
                                    'Nessuna squadra presente'
                                }
                            </Typography>
                            {(searchTerm || filterLega !== 'all' || filterSport !== 'all' || filterStatus !== 'all') && (
                                <Button
                                    variant="outlined"
                                    onClick={resetFilters}
                                    sx={{ mt: 2 }}
                                >
                                    Cancella filtri
                                </Button>
                            )}
                        </Box>
                    )}
                </Card>

                {/* Dialog dettagli squadra */}
                <Dialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            src={selectedTeam?.foto ? `${host}/image-proxy-mw?url=${encodeURIComponent(selectedTeam.foto)}` : undefined}
                            alt={selectedTeam?.teamNome}
                            sx={{ width: 50, height: 50 }}
                        >
                            {selectedTeam?.teamNome?.charAt(0).toUpperCase() || '⚽'}
                        </Avatar>
                        <Box>
                            <Typography variant="h6">
                                {selectedTeam?.teamNome || 'Squadra senza nome'}
                            </Typography>
                            {/* <Typography variant="caption" color="text.secondary">
                                ID: {selectedTeam?.teamId} • {selectedTeam?.legaDescrizione} • {selectedTeam?.sportNome}
                            </Typography> */}
                        </Box>
                    </DialogTitle>

                    <DialogContent>
                        {selectedTeam && (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Informazioni Squadra
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Nome:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedTeam.teamNome || 'N/A'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Team ID:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedTeam.teamId || 'N/A'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Ordinamento:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedTeam.ordinamento || 'Non impostato'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Lega
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body2" color="text.secondary">Nome:</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <EmojiEventsIcon sx={{ fontSize: 20, color: '#1565c0' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {selectedTeam.legaDescrizione || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Lega ID:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedTeam.legaId}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Sport
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body2" color="text.secondary">Nome:</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <SportsIcon sx={{ fontSize: 20, color: '#e65100' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {selectedTeam.sportNome || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Sport ID:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedTeam.sportId}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Stato e Flags
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body2" color="text.secondary">Visibile:</Typography>
                                                <Chip
                                                    label={selectedTeam.flVisibile ? 'Sì' : 'No'}
                                                    size="small"
                                                    color={selectedTeam.flVisibile ? 'success' : 'default'}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body2" color="text.secondary">Attiva:</Typography>
                                                <Chip
                                                    label={selectedTeam.flAttivo ? 'Sì' : 'No'}
                                                    size="small"
                                                    color={selectedTeam.flAttivo ? 'success' : 'warning'}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body2" color="text.secondary">Annullata:</Typography>
                                                <Chip
                                                    label={selectedTeam.flAnnullato ? 'Sì' : 'No'}
                                                    size="small"
                                                    color={selectedTeam.flAnnullato ? 'error' : 'success'}
                                                />
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Timestamp
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Creata il:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedTeam.createdAt ?
                                                        new Date(selectedTeam.createdAt).toLocaleString('it-IT') :
                                                        'N/A'
                                                    }
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Aggiornata il:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedTeam.updatedAt ?
                                                        new Date(selectedTeam.updatedAt).toLocaleString('it-IT') :
                                                        'N/A'
                                                    }
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>

                                {selectedTeam.note && (
                                    <Grid item xs={12}>
                                        <Card sx={{ p: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Note Squadra
                                            </Typography>
                                            <Typography variant="body2" sx={{
                                                bgcolor: '#f5f5f5',
                                                p: 2,
                                                borderRadius: 1,
                                                fontStyle: 'italic'
                                            }}>
                                                {selectedTeam.note}
                                            </Typography>
                                        </Card>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>
                            Chiudi
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                handleEditTeam(selectedTeam);
                                setDialogOpen(false);
                            }}
                        >
                            Modifica
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Footer con info */}
                {teams.length > 0 && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Ultimo aggiornamento: {new Date().toLocaleString('it-IT')}
                            {autoRefresh && " • Auto refresh attivo"}
                            • Pagina {page + 1} di {Math.ceil(total / limit)}
                            {(searchTerm || filterLega !== 'all' || filterSport !== 'all' || filterStatus !== 'all') &&
                                ` • Filtri applicati`
                            }
                        </Typography>
                    </Box>
                )}

            </motion.div>
        </Box>
    );
}

export default SquadrePage;